import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { UserVideo, Skill } from '../../types';
import SkillSelector from '../common/SkillSelector';
import { genericAPIs } from '../../services/api';

interface EditVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: UserVideo | null;
  onSave: (videoId: string, updatedData: { title: string; description: string; tags: string[] }) => Promise<void>;
}

const EditVideoModal: React.FC<EditVideoModalProps> = ({
  isOpen,
  onClose,
  video,
  onSave,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState<Skill[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSkills();
    }
  }, [isOpen]);

  useEffect(() => {
    if (video && skills.length > 0) {
      setTitle(video.title || '');
      setDescription(video.description || '');

      // Match existing video.tags (strings) to skills list
      if (video.tags && video.tags.length > 0) {
        const matched = skills.filter((s) =>
          video.tags.some(
            (t) =>
              t.toLowerCase() === s.name.toLowerCase() ||
              t === s.id.toString()
          )
        );
        setSelectedTags(matched);
      } else {
        setSelectedTags([]);
      }
    }
  }, [video, skills, isOpen]);

  const fetchSkills = async () => {
    try {
      const response = await genericAPIs.getUserRoleSkills('all');
      setSkills(response.data?.data?.skills || []);
    } catch (error) {
      console.error('Failed to fetch skills', error);
    }
  };

  const handleTagsChange = (skillIds: number[]) => {
    const selected = skills.filter((s) => skillIds.includes(s.id));
    setSelectedTags(selected);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!video || !title.trim()) return;

    setSaving(true);
    try {
      await onSave(video.id, {
        title: title.trim(),
        description: description.trim(),
        tags: selectedTags.map((s) => s.name),
      });
      onClose();
    } catch (err) {
      console.error('Failed to update video', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 text-left align-middle shadow-2xl transition-all border border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-800">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-bold leading-6 text-gray-900 dark:text-white"
                  >
                    Edit Video Details
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-500 transition"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Title
                    </label>
                    <input
                      type="text"
                      required
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Video Title"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:outline-none focus:border-gray-300 focus:ring-0 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Optional description"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:outline-none focus:border-gray-300 focus:ring-0 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tags (Specializations)
                    </label>
                    <SkillSelector
                      options={skills}
                      selectedSkillIds={selectedTags.map((s) => s.id)}
                      onChange={handleTagsChange}
                      maxSkills={5}
                    />
                  </div>

                  <div className="mt-6 flex justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving || !title.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditVideoModal;
